import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import { fetchPublicPapers, type ConferencePaperPublic } from "./api";
import { formatDate } from "./format";

/** Compact papers / conference-archive teaser for the landing page. */
export function RecentPapersTeaser() {
  const { t, locale } = useLanguage();
  const [rows, setRows] = useState<ConferencePaperPublic[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchPublicPapers({ limit: 3, offset: 0 })
      .then((res) => {
        if (!cancelled) setRows(res.data);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <section className="dn-section" id="papers-archive">
        <h2>{t.sections.papersArchive}</h2>
        <p className="dn-muted">{t.common.loading}</p>
      </section>
    );
  }

  if (rows.length === 0) {
    return (
      <section className="dn-section" id="papers-archive">
        <h2>{t.sections.papersArchive}</h2>
        <p className="lead">{t.sections.papersArchiveBody}</p>
        <Link className="dn-btn dn-btn-primary" to="/papers">
          {t.sections.browseAllPapers}
        </Link>
      </section>
    );
  }

  return (
    <section className="dn-section" id="papers-archive">
      <h2>{t.sections.papersArchive}</h2>
      <p className="lead">{t.sections.papersArchiveBody}</p>
      <div className="dn-grid">
        {rows.map((paper) => (
          <article key={paper.id} className="dn-card">
            <p className="dn-paper-edition">
              {locale === "ar"
                ? `${paper.conference_title_ar} — ${paper.conference_year}`
                : `${paper.conference_title_en} — ${paper.conference_year}`}
            </p>
            <h3>{locale === "ar" && paper.title_alt ? paper.title_alt : paper.title}</h3>
            <p className="dn-muted">{paper.authors}</p>
            <p className="dn-muted">{formatDate(paper.published_at || paper.created_at, locale)}</p>
            {paper.is_external_archive && (
              <p className="dn-paper-archive-badge">{t.papers.externalArchive}</p>
            )}
          </article>
        ))}
      </div>
      <div style={{ marginTop: "1rem" }}>
        <Link className="dn-btn dn-btn-primary" to="/papers">
          {t.sections.browseAllPapers}
        </Link>
      </div>
    </section>
  );
}
